import { CommonModule } from '@angular/common';
import { Component, input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LucideCircleQuestionMark } from '@lucide/angular';
import moment from 'moment';
import { SalaryScale, SalaryScaleEntry } from '../salary-scale.model';

export type QualificationLevelSalaryScaleEntryFormGroup = FormGroup<{
  id: FormControl<number | undefined>;
  grossHourlySalary: FormControl<number>;
  qualificationLevel: FormControl<string>;
}>;

@Component({
  selector: 'app-salary-scale-form',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatIconModule,
    LucideCircleQuestionMark,
    MatTooltipModule,
  ],
  templateUrl: './salary-scale-form.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./salary-scale-form.scss'],
})
export class SalaryScaleForm implements OnInit {
  initWith = input.required<SalaryScale>();
  editable = input.required<boolean>();

  formGroup = new FormGroup({
    startDate: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    entries: new FormArray<QualificationLevelSalaryScaleEntryFormGroup>([
      this.buildQualificationLevelSalaryScaleEntryFormGroup(),
    ]),
  });

  private buildQualificationLevelSalaryScaleEntryFormGroup(
    salaryScaleEntry?: SalaryScaleEntry,
  ): QualificationLevelSalaryScaleEntryFormGroup {
    return new FormGroup({
      id: new FormControl<number | undefined>(salaryScaleEntry?.id, {
        nonNullable: true,
      }),
      grossHourlySalary: new FormControl<number>(salaryScaleEntry?.grossHourlySalary ?? 0, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0)],
      }),
      qualificationLevel: new FormControl<string>(salaryScaleEntry?.qualificationLevel ?? '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });
  }

  ngOnInit(): void {
    const initWith = this.initWith();
    if (initWith) {
      this.formGroup.patchValue({
        startDate: moment(initWith.startDate).format('YYYY-MM-DD'),
      });
      this.formGroup.setControl(
        'entries',
        new FormArray<QualificationLevelSalaryScaleEntryFormGroup>(
          initWith.entries.map((entry) =>
            this.buildQualificationLevelSalaryScaleEntryFormGroup(entry),
          ) ?? [],
        ),
      );
    }
    if (!this.editable()) {
      this.formGroup.disable();
    }
  }
}
